import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Plus,
  Download,
  Edit2,
  Trash2,
  Send,
  Copy,
  CreditCard,
  Settings,
  ArrowLeft,
  Check,
  X,
  Upload,
  DollarSign,
  AlertTriangle,
  Clock,
  Bell,
  Euro,
  Layout,
  Palette,
  Star,
  Mail,
  MailOpen,
  MailX,
  Eye,
  Pencil,
  User,
} from 'lucide-react';
import useStore from '../store/useStore';
import TemplateEditor from '../components/TemplateEditor';
import AdvancedTemplateEditor from '../components/AdvancedTemplateEditor';

// Import refactored components
import {
  apiCall,
  downloadInvoicePdf,
  EmailSendModal,
  PaymentModal,
  ReminderModal,
  ClientsTab,
  InvoiceModal,
  SettingsModal,
} from '../components/invoicing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Status badge component
const StatusBadge = ({ status, language }) => {
  const statusConfig = {
    draft: { bg: 'bg-gray-100', text: 'text-gray-700', label: { fr: 'Brouillon', nl: 'Concept' } },
    sent: { bg: 'bg-blue-100', text: 'text-blue-700', label: { fr: 'Envoyée', nl: 'Verzonden' } },
    partial: { bg: 'bg-orange-100', text: 'text-orange-700', label: { fr: 'Partiel', nl: 'Gedeeltelijk' } },
    paid: { bg: 'bg-green-100', text: 'text-green-700', label: { fr: 'Payée', nl: 'Betaald' } },
    cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: { fr: 'Annulée', nl: 'Geannuleerd' } },
  };
  const config = statusConfig[status] || statusConfig.draft;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// Send Status Badge with dropdown
const SendStatusBadge = ({ status, language, onChangeStatus }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  
  const statusConfig = {
    not_sent: { 
      bg: 'bg-red-100', 
      text: 'text-red-700', 
      icon: MailX,
      label: { fr: 'Non envoyée', nl: 'Niet verzonden' } 
    },
    pending: { 
      bg: 'bg-orange-100', 
      text: 'text-orange-700', 
      icon: Mail,
      label: { fr: 'En attente', nl: 'In afwachting' } 
    },
    sent: { 
      bg: 'bg-green-100', 
      text: 'text-green-700', 
      icon: MailOpen,
      label: { fr: 'Envoyée', nl: 'Verzonden' } 
    },
  };
  
  const config = statusConfig[status] || statusConfig.not_sent;
  const Icon = config.icon;
  
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${config.bg} ${config.text} hover:opacity-80`}
      >
        <Icon className="w-3 h-3" />
        {config.label[language]}
      </button>
      
      {showDropdown && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
          <div className="absolute right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 py-1 min-w-[140px]">
            {Object.entries(statusConfig).map(([key, cfg]) => {
              const StatusIcon = cfg.icon;
              return (
                <button
                  key={key}
                  onClick={() => {
                    onChangeStatus(key);
                    setShowDropdown(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 ${
                    status === key ? 'bg-gray-100' : ''
                  }`}
                >
                  <StatusIcon className={`w-4 h-4 ${cfg.text}`} />
                  <span className={cfg.text}>{cfg.label[language]}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const InvoicingPage = () => {
  const navigate = useNavigate();
  const { language } = useStore();
  
  const [activeTab, setActiveTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [settings, setSettings] = useState({});
  const [vatRates, setVatRates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState({});
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingCreditNote, setCreatingCreditNote] = useState(false); // Pour créer une note de crédit
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [showAdvancedEditor, setShowAdvancedEditor] = useState(false);
  const [advancedEditorType, setAdvancedEditorType] = useState('invoice'); // 'invoice' or 'credit_note'
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [sendStatusFilter, setSendStatusFilter] = useState('all');
  const [sendStatusCounts, setSendStatusCounts] = useState({ not_sent: 0, pending: 0, sent: 0 });
  const [emailStatus, setEmailStatus] = useState({ configured: false });
  const [sendingEmail, setSendingEmail] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null); // For invoice detail modal

  // Check admin auth
  useEffect(() => {
    const isAdmin = localStorage.getItem('ebb_admin');
    if (!isAdmin) {
      navigate('/admin');
    }
  }, [navigate]);

  // Load email status
  useEffect(() => {
    const checkEmailStatus = async () => {
      try {
        const status = await apiCall('/api/email/status');
        setEmailStatus(status);
      } catch (e) {
        console.log('Email service not configured');
      }
    };
    checkEmailStatus();
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [invoicesData, settingsData, ratesData, templatesData, summaryData, overdueData, sendStatusData] = await Promise.all([
        apiCall('/api/invoicing/invoices'),
        apiCall('/api/invoicing/settings'),
        apiCall('/api/invoicing/vat-rates'),
        apiCall('/api/invoicing/templates'),
        apiCall('/api/invoicing/payment-summary'),
        apiCall('/api/invoicing/overdue'),
        apiCall('/api/invoicing/invoices-by-send-status'),
      ]);
      setInvoices(invoicesData.invoices || []);
      setSettings(settingsData);
      setVatRates(ratesData.rates || []);
      setTemplates(templatesData.templates || []);
      // Filter custom templates
      setCustomTemplates((templatesData.templates || []).filter(t => t.is_custom));
      setPaymentSummary(summaryData);
      setOverdueInvoices(overdueData.invoices || []);
      setSendStatusCounts(sendStatusData.counts || { not_sent: 0, pending: 0, sent: 0 });
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const token = localStorage.getItem('ebb_token');
      const response = await fetch(`${BACKEND_URL}/api/invoicing/invoices/${invoiceId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer cette facture ?' : 'Deze factuur verwijderen?')) return;
    try {
      await apiCall(`/api/invoicing/invoices/${invoiceId}`, { method: 'DELETE' });
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDuplicateInvoice = async (invoiceId) => {
    try {
      await apiCall(`/api/invoicing/invoices/${invoiceId}/duplicate`, { method: 'POST' });
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCreateCreditNote = async (invoiceId) => {
    try {
      await apiCall(`/api/invoicing/invoices/${invoiceId}/credit-note`, { method: 'POST' });
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await apiCall(`/api/invoicing/invoices/${invoiceId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  // Email sending functions
  const handleSendInvoiceEmail = async (invoice, includePdf = true) => {
    if (!emailStatus.configured) {
      alert(language === 'fr' 
        ? 'Service email non configuré. Ajoutez SENDGRID_API_KEY et SENDER_EMAIL dans backend/.env'
        : 'E-mailservice niet geconfigureerd. Voeg SENDGRID_API_KEY en SENDER_EMAIL toe aan backend/.env');
      return;
    }
    
    if (!invoice.customer?.email) {
      alert(language === 'fr' ? 'Adresse email du client manquante' : 'E-mailadres van klant ontbreekt');
      return;
    }
    
    setSendingEmail(invoice.id);
    try {
      const result = await apiCall(`/api/invoicing/invoices/${invoice.id}/send-email`, {
        method: 'POST',
        body: JSON.stringify({ language, include_pdf: includePdf }),
      });
      alert(result.message || (language === 'fr' ? 'Email envoyé !' : 'E-mail verzonden!'));
      loadData();
    } catch (error) {
      alert(error.message);
    } finally {
      setSendingEmail(null);
      setShowEmailModal(null);
    }
  };

  const handleSendReminderEmail = async (invoice, reminderType = 'first', includePdf = false) => {
    if (!emailStatus.configured) {
      alert(language === 'fr' 
        ? 'Service email non configuré. Ajoutez SENDGRID_API_KEY et SENDER_EMAIL dans backend/.env'
        : 'E-mailservice niet geconfigureerd. Voeg SENDGRID_API_KEY en SENDER_EMAIL toe aan backend/.env');
      return;
    }
    
    if (!invoice.customer?.email) {
      alert(language === 'fr' ? 'Adresse email du client manquante' : 'E-mailadres van klant ontbreekt');
      return;
    }
    
    setSendingEmail(invoice.id);
    try {
      const result = await apiCall(`/api/invoicing/invoices/${invoice.id}/send-reminder-email`, {
        method: 'POST',
        body: JSON.stringify({ language, reminder_type: reminderType, include_pdf: includePdf }),
      });
      alert(result.message || (language === 'fr' ? 'Rappel envoyé !' : 'Herinnering verzonden!'));
      loadData();
    } catch (error) {
      alert(error.message);
    } finally {
      setSendingEmail(null);
    }
  };

  // Template management functions
  const handleSaveTemplate = async (templateData) => {
    try {
      // DEBUG - Log complet
      console.log('========== INVOICING PAGE SAVE ==========');
      console.log('Received from TemplateEditor:', JSON.stringify(templateData, null, 2));
      console.log('editingTemplate?.id:', editingTemplate?.id);
      console.log('Is Update?', !!editingTemplate?.id);
      console.log('=========================================');
      
      if (editingTemplate?.id) {
        // Update existing template
        console.log('Calling PUT /api/invoicing/custom-templates/' + editingTemplate.id);
        const result = await apiCall(`/api/invoicing/custom-templates/${editingTemplate.id}`, {
          method: 'PUT',
          body: JSON.stringify(templateData),
        });
        console.log('PUT result:', result);
      } else {
        // Create new template
        console.log('Calling POST /api/invoicing/custom-templates');
        const result = await apiCall('/api/invoicing/custom-templates', {
          method: 'POST',
          body: JSON.stringify(templateData),
        });
        console.log('POST result:', result);
      }
      setShowTemplateEditor(false);
      setEditingTemplate(null);
      loadData();
    } catch (error) {
      console.error('Save error:', error);
      alert(error.message);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer ce template ?' : 'Dit sjabloon verwijderen?')) return;
    try {
      await apiCall(`/api/invoicing/custom-templates/${templateId}`, { method: 'DELETE' });
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleSetDefaultTemplate = async (templateId) => {
    try {
      await apiCall(`/api/invoicing/custom-templates/${templateId}/set-default`, { method: 'POST' });
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  // Send status management
  const handleUpdateSendStatus = async (invoiceId, newStatus) => {
    try {
      await apiCall(`/api/invoicing/invoices/${invoiceId}/send-status`, {
        method: 'PUT',
        body: JSON.stringify({ send_status: newStatus }),
      });
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  // Filter invoices by send status
  const filteredInvoices = sendStatusFilter === 'all' 
    ? invoices 
    : invoices.filter(inv => inv.send_status === sendStatusFilter);

  const t = {
    fr: {
      title: 'Facturation',
      invoices: 'Factures',
      creditNotes: 'Notes de crédit',
      overdue: 'En retard',
      settings: 'Paramètres',
      templates: 'Templates',
      newInvoice: 'Nouvelle facture',
      newCreditNote: 'Nouvelle note de crédit',
      number: 'Numéro',
      date: 'Date',
      dueDate: 'Échéance',
      customer: 'Client',
      amount: 'Montant TTC',
      paid: 'Payé',
      due: 'Dû',
      status: 'Statut',
      sendStatus: 'Envoi',
      actions: 'Actions',
      download: 'Télécharger PDF',
      edit: 'Modifier',
      delete: 'Supprimer',
      duplicate: 'Dupliquer',
      createCreditNote: 'Créer note de crédit',
      markAsSent: 'Marquer envoyée',
      markAsPaid: 'Marquer payée',
      addPayment: 'Ajouter paiement',
      sendReminder: 'Envoyer rappel',
      totalInvoiced: 'Total facturé',
      totalPaid: 'Total encaissé',
      totalDue: 'Total dû',
      overdueCount: 'Factures en retard',
      daysOverdue: 'jours de retard',
      noInvoices: 'Aucune facture',
      backToAdmin: 'Retour',
      qrCodeInfo: 'Les factures incluent un QR-Code bancaire belge',
      // New translations
      newTemplate: 'Nouveau template',
      editTemplate: 'Modifier template',
      deleteTemplate: 'Supprimer template',
      setDefault: 'Définir par défaut',
      defaultTemplate: 'Par défaut',
      predefined: 'Prédéfini',
      custom: 'Personnalisé',
      all: 'Toutes',
      notSent: 'Non envoyée',
      pending: 'En attente',
      sent: 'Envoyée',
      autoGenerated: 'Auto-générée',
      filterByStatus: 'Filtrer par statut',
      markAsNotSent: 'Marquer non envoyée',
      markAsPending: 'Marquer en attente',
      preview: 'Aperçu',
      // Email translations
      sendByEmail: 'Envoyer par email',
      sendInvoice: 'Envoyer facture',
      sendReminderEmail: 'Envoyer rappel par email',
      emailNotConfigured: 'Service email non configuré',
      emailConfigured: 'Service email configuré',
      includePdf: 'Joindre PDF',
      firstReminder: '1er rappel',
      secondReminder: '2ème rappel',
      formalNotice: 'Mise en demeure',
      sending: 'Envoi en cours...',
      confirmSendEmail: 'Envoyer cette facture par email à',
    },
    nl: {
      title: 'Facturatie',
      invoices: 'Facturen',
      creditNotes: 'Creditnota\'s',
      overdue: 'Achterstallig',
      settings: 'Instellingen',
      templates: 'Sjablonen',
      newInvoice: 'Nieuwe factuur',
      newCreditNote: 'Nieuwe creditnota',
      number: 'Nummer',
      date: 'Datum',
      dueDate: 'Vervaldatum',
      customer: 'Klant',
      amount: 'Bedrag incl.',
      paid: 'Betaald',
      due: 'Te betalen',
      status: 'Status',
      sendStatus: 'Verzending',
      actions: 'Acties',
      download: 'PDF downloaden',
      edit: 'Bewerken',
      delete: 'Verwijderen',
      duplicate: 'Dupliceren',
      createCreditNote: 'Creditnota maken',
      markAsSent: 'Markeren als verzonden',
      markAsPaid: 'Markeren als betaald',
      addPayment: 'Betaling toevoegen',
      sendReminder: 'Herinnering sturen',
      totalInvoiced: 'Totaal gefactureerd',
      totalPaid: 'Totaal ontvangen',
      totalDue: 'Totaal te betalen',
      overdueCount: 'Achterstallige facturen',
      daysOverdue: 'dagen achterstallig',
      noInvoices: 'Geen facturen',
      backToAdmin: 'Terug',
      qrCodeInfo: 'Facturen bevatten een Belgische bank QR-code',
      // New translations
      newTemplate: 'Nieuw sjabloon',
      editTemplate: 'Sjabloon bewerken',
      deleteTemplate: 'Sjabloon verwijderen',
      setDefault: 'Als standaard instellen',
      defaultTemplate: 'Standaard',
      predefined: 'Voorgedefinieerd',
      custom: 'Aangepast',
      all: 'Alle',
      notSent: 'Niet verzonden',
      pending: 'In afwachting',
      sent: 'Verzonden',
      autoGenerated: 'Auto-gegenereerd',
      filterByStatus: 'Filteren op status',
      markAsNotSent: 'Markeren als niet verzonden',
      markAsPending: 'Markeren als in afwachting',
      preview: 'Voorbeeld',
      // Email translations
      sendByEmail: 'Verzenden per e-mail',
      sendInvoice: 'Factuur verzenden',
      sendReminderEmail: 'Herinnering per e-mail',
      emailNotConfigured: 'E-mailservice niet geconfigureerd',
      emailConfigured: 'E-mailservice geconfigureerd',
      includePdf: 'PDF bijvoegen',
      firstReminder: '1e herinnering',
      secondReminder: '2e herinnering',
      formalNotice: 'Ingebrekestelling',
      sending: 'Verzenden...',
      confirmSendEmail: 'Deze factuur per e-mail verzenden naar',
    },
  }[language];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-amber-900 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="text-white hover:text-amber-200 transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <FileText className="w-7 h-7" />
                  {t.title}
                </h1>
                <p className="text-amber-200 text-xs mt-1">{t.qrCodeInfo}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowSettingsModal(true)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4" />
                {t.settings}
              </button>
              {activeTab === 'credit-notes' ? (
                <button
                  onClick={() => {
                    setEditingInvoice(null);
                    setCreatingCreditNote(true);
                    setShowCreateModal(true);
                  }}
                  className="flex items-center gap-2 bg-purple-100 text-purple-900 px-4 py-2 rounded-lg font-medium hover:bg-purple-200 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {t.newCreditNote}
                </button>
              ) : (
                <button
                  onClick={() => {
                    setEditingInvoice(null);
                    setCreatingCreditNote(false);
                    setShowCreateModal(true);
                  }}
                  className="flex items-center gap-2 bg-white text-amber-900 px-4 py-2 rounded-lg font-medium hover:bg-amber-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {t.newInvoice}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Euro className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t.totalInvoiced}</p>
                <p className="text-xl font-bold text-gray-900">€{(paymentSummary.total_invoiced || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t.totalPaid}</p>
                <p className="text-xl font-bold text-green-600">€{(paymentSummary.total_paid || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t.totalDue}</p>
                <p className="text-xl font-bold text-orange-600">€{(paymentSummary.total_due || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${(paymentSummary.overdue_count || 0) > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                <AlertTriangle className={`w-5 h-5 ${(paymentSummary.overdue_count || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t.overdueCount}</p>
                <p className={`text-xl font-bold ${(paymentSummary.overdue_count || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {paymentSummary.overdue_count || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('invoices')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'invoices'
                    ? 'border-amber-700 text-amber-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                {t.invoices} ({paymentSummary.status_counts?.draft || 0} + {paymentSummary.status_counts?.sent || 0} + {paymentSummary.status_counts?.partial || 0})
              </button>
              <button
                onClick={() => setActiveTab('overdue')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'overdue'
                    ? 'border-amber-700 text-amber-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                {t.overdue}
                {(paymentSummary.overdue_count || 0) > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {paymentSummary.overdue_count}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('credit-notes')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'credit-notes'
                    ? 'border-amber-700 text-amber-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <CreditCard className="w-4 h-4 inline mr-2" />
                {t.creditNotes}
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'templates'
                    ? 'border-amber-700 text-amber-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Layout className="w-4 h-4 inline mr-2" />
                {t.templates}
              </button>
              <button
                onClick={() => setActiveTab('clients')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'clients'
                    ? 'border-amber-700 text-amber-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <User className="w-4 h-4 inline mr-2" />
                {language === 'fr' ? 'Clients' : 'Klanten'}
              </button>
            </nav>
          </div>

          {/* Clients Tab Content */}
          {activeTab === 'clients' && (
            <ClientsTab language={language} />
          )}

          {/* Templates Tab Content */}
          {activeTab === 'templates' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold">{t.templates}</h2>
                <div className="flex gap-2">
                  {/* Advanced Editor buttons */}
                  <button
                    onClick={() => {
                      setEditingTemplate(null);
                      setAdvancedEditorType('invoice');
                      setShowAdvancedEditor(true);
                    }}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    data-testid="advanced-invoice-editor-btn"
                  >
                    <Pencil className="w-4 h-4" />
                    {language === 'fr' ? 'Éditeur Avancé Facture' : 'Geavanceerde Factuur Editor'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingTemplate(null);
                      setAdvancedEditorType('credit_note');
                      setShowAdvancedEditor(true);
                    }}
                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                    data-testid="advanced-credit-note-editor-btn"
                  >
                    <Pencil className="w-4 h-4" />
                    {language === 'fr' ? 'Éditeur Note de Crédit' : 'Creditnota Editor'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingTemplate(null);
                      setShowTemplateEditor(true);
                    }}
                    className="flex items-center gap-2 bg-amber-700 text-white px-4 py-2 rounded-lg hover:bg-amber-800 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t.newTemplate}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={`border rounded-lg overflow-hidden hover:shadow-lg transition-shadow ${
                      template.is_default ? 'ring-2 ring-amber-500' : ''
                    }`}
                  >
                    {/* Template Preview Header */}
                    <div
                      className="h-24 flex items-center justify-center relative"
                      style={{ backgroundColor: template.primary_color || '#8B4513' }}
                    >
                      <span className="text-white text-lg font-bold opacity-50">
                        {language === 'fr' ? 'FACTURE' : 'FACTUUR'}
                      </span>
                      {template.is_default && (
                        <span className="absolute top-2 right-2 bg-amber-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          {t.defaultTemplate}
                        </span>
                      )}
                    </div>

                    {/* Template Info */}
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900">
                        {language === 'nl' && template.name_nl ? template.name_nl : template.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {language === 'nl' && template.description_nl ? template.description_nl : template.description}
                      </p>

                      {/* Color Preview */}
                      <div className="flex gap-2 mt-3">
                        <div
                          className="w-6 h-6 rounded border"
                          style={{ backgroundColor: template.primary_color }}
                          title="Primary"
                        />
                        <div
                          className="w-6 h-6 rounded border"
                          style={{ backgroundColor: template.secondary_color }}
                          title="Secondary"
                        />
                        <div
                          className="w-6 h-6 rounded border"
                          style={{ backgroundColor: template.accent_color }}
                          title="Accent"
                        />
                      </div>

                      {/* Actions - TOUJOURS VISIBLES */}
                      <div className="flex gap-2 mt-4">
                        {!template.is_default && (
                          <button
                            onClick={() => handleSetDefaultTemplate(template.id)}
                            className="flex-1 text-sm px-3 py-2 border border-amber-600 text-amber-600 rounded hover:bg-amber-50 transition-colors"
                          >
                            {t.setDefault}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditingTemplate(template);
                            setShowTemplateEditor(true);
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                          title={t.edit}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title={t.delete}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Send Status Filter (for invoices tab) */}
          {activeTab === 'invoices' && (
            <div className="px-6 py-3 bg-gray-50 border-b flex items-center gap-4">
              <span className="text-sm text-gray-600">{t.filterByStatus}:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSendStatusFilter('all')}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                    sendStatusFilter === 'all'
                      ? 'bg-gray-700 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {t.all} ({invoices.length})
                </button>
                <button
                  onClick={() => setSendStatusFilter('not_sent')}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
                    sendStatusFilter === 'not_sent'
                      ? 'bg-red-600 text-white'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                >
                  <MailX className="w-3 h-3" />
                  {t.notSent} ({sendStatusCounts.not_sent || 0})
                </button>
                <button
                  onClick={() => setSendStatusFilter('pending')}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
                    sendStatusFilter === 'pending'
                      ? 'bg-orange-600 text-white'
                      : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                  }`}
                >
                  <Mail className="w-3 h-3" />
                  {t.pending} ({sendStatusCounts.pending || 0})
                </button>
                <button
                  onClick={() => setSendStatusFilter('sent')}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
                    sendStatusFilter === 'sent'
                      ? 'bg-green-600 text-white'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  <MailOpen className="w-3 h-3" />
                  {t.sent} ({sendStatusCounts.sent || 0})
                </button>
              </div>
            </div>
          )}

          {/* Invoice List */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-700 border-t-transparent mx-auto"></div>
              </div>
            ) : activeTab === 'overdue' ? (
              // Overdue invoices
              overdueInvoices.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {language === 'fr' ? 'Aucune facture en retard' : 'Geen achterstallige facturen'}
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase">{t.number}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase">{t.customer}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase">{t.dueDate}</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-red-700 uppercase">{t.daysOverdue}</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-red-700 uppercase">{t.due}</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-red-700 uppercase">{t.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {overdueInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-red-50/50">
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{invoice.invoice_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoice.customer?.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.due_date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                            {invoice.days_overdue} {language === 'fr' ? 'j' : 'd'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-red-600">
                          €{(invoice.amount_due || invoice.total_incl || 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => setShowPaymentModal(invoice)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title={t.addPayment}
                            >
                              <DollarSign className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setShowReminderModal(invoice)}
                              className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
                              title={t.sendReminder}
                            >
                              <Bell className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)}
                              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                              title={t.download}
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : invoices.filter(inv => activeTab === 'credit-notes' ? inv.is_credit_note : !inv.is_credit_note).length === 0 ? (
              <div className="p-8 text-center text-gray-500">{t.noInvoices}</div>
            ) : activeTab !== 'templates' && (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.number}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.date}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.customer}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t.amount}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t.paid}</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t.status}</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t.sendStatus}</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(activeTab === 'invoices' ? filteredInvoices : invoices)
                    .filter(inv => activeTab === 'credit-notes' ? inv.is_credit_note : !inv.is_credit_note)
                    .map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => setSelectedInvoice(invoice)}
                            className="font-medium text-amber-700 hover:text-amber-800 hover:underline"
                          >
                            {invoice.invoice_number}
                          </button>
                          {invoice.auto_generated && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title={t.autoGenerated}>
                              Auto
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.invoice_date}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{invoice.customer?.name}</div>
                          <div className="text-xs text-gray-500">{invoice.customer?.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">
                          €{(invoice.total_incl || 0).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <span className={invoice.amount_paid > 0 ? 'text-green-600' : 'text-gray-400'}>
                            €{(invoice.amount_paid || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <StatusBadge status={invoice.status} language={language} />
                          {invoice.reminders_sent?.length > 0 && (
                            <span className="ml-1 text-orange-500" title={`${invoice.reminders_sent.length} rappel(s)`}>
                              <Bell className="w-3 h-3 inline" />
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <SendStatusBadge 
                            status={invoice.send_status || 'not_sent'} 
                            language={language}
                            onChangeStatus={(newStatus) => handleUpdateSendStatus(invoice.id, newStatus)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex justify-end gap-1">
                            {/* View detail button */}
                            <button
                              onClick={() => setSelectedInvoice(invoice)}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                              title={t.preview}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)}
                              className="p-1.5 text-gray-500 hover:text-amber-700 hover:bg-amber-50 rounded"
                              title={t.download}
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            {/* Send by Email button - always visible */}
                            <button
                              onClick={() => {
                                if (!invoice.customer?.email) {
                                  alert(language === 'fr' 
                                    ? 'Aucune adresse email pour ce client. Modifiez la facture pour ajouter un email.'
                                    : 'Geen e-mailadres voor deze klant. Bewerk de factuur om een e-mail toe te voegen.');
                                  return;
                                }
                                if (!emailStatus.configured) {
                                  alert(language === 'fr'
                                    ? 'Service email non configuré. Configurez SMTP dans backend/.env'
                                    : 'E-mailservice niet geconfigureerd. Configureer SMTP in backend/.env');
                                  return;
                                }
                                setShowEmailModal(invoice);
                              }}
                              disabled={sendingEmail === invoice.id}
                              className={`p-1.5 rounded ${
                                sendingEmail === invoice.id 
                                  ? 'text-gray-400 cursor-wait' 
                                  : !invoice.customer?.email
                                    ? 'text-gray-300 hover:text-gray-400 hover:bg-gray-50'
                                    : emailStatus.configured 
                                      ? 'text-gray-500 hover:text-blue-700 hover:bg-blue-50' 
                                      : 'text-orange-400 hover:text-orange-600 hover:bg-orange-50'
                              }`}
                              title={
                                !invoice.customer?.email 
                                  ? (language === 'fr' ? 'Pas d\'email client' : 'Geen klant e-mail')
                                  : emailStatus.configured 
                                    ? t.sendByEmail 
                                    : t.emailNotConfigured
                              }
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                            {!invoice.is_credit_note && invoice.status !== 'paid' && (
                              <button
                                onClick={() => setShowPaymentModal(invoice)}
                                className="p-1.5 text-gray-500 hover:text-green-700 hover:bg-green-50 rounded"
                                title={t.addPayment}
                              >
                                <DollarSign className="w-4 h-4" />
                              </button>
                            )}
                            {invoice.status === 'draft' && (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingInvoice(invoice);
                                    setShowCreateModal(true);
                                  }}
                                  className="p-1.5 text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                                  title={t.edit}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(invoice.id, 'sent')}
                                  className="p-1.5 text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                                  title={t.markAsSent}
                                >
                                  <Send className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteInvoice(invoice.id)}
                                  className="p-1.5 text-gray-500 hover:text-red-700 hover:bg-red-50 rounded"
                                  title={t.delete}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {(invoice.status === 'sent' || invoice.status === 'partial') && (
                              <>
                                <button
                                  onClick={() => setShowReminderModal(invoice)}
                                  className="p-1.5 text-gray-500 hover:text-orange-700 hover:bg-orange-50 rounded"
                                  title={t.sendReminder}
                                >
                                  <Bell className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDuplicateInvoice(invoice.id)}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                              title={t.duplicate}
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            {!invoice.is_credit_note && invoice.status !== 'draft' && (
                              <button
                                onClick={() => handleCreateCreditNote(invoice.id)}
                                className="p-1.5 text-gray-500 hover:text-purple-700 hover:bg-purple-50 rounded"
                                title={t.createCreditNote}
                              >
                                <CreditCard className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Invoice Modal */}
      {showCreateModal && (
        <InvoiceModal
          invoice={editingInvoice}
          templates={templates}
          vatRates={vatRates}
          language={language}
          defaultTemplateId={settings?.default_template}
          isCreditNote={creatingCreditNote}
          onClose={() => {
            setShowCreateModal(false);
            setEditingInvoice(null);
            setCreatingCreditNote(false);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setEditingInvoice(null);
            setCreatingCreditNote(false);
            loadData();
          }}
        />
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <SettingsModal
          settings={settings}
          vatRates={vatRates}
          language={language}
          onClose={() => setShowSettingsModal(false)}
          onSave={() => {
            setShowSettingsModal(false);
            loadData();
          }}
        />
      )}

      {/* Email Send Modal */}
      {showEmailModal && (
        <EmailSendModal
          invoice={showEmailModal}
          language={language}
          emailStatus={emailStatus}
          onClose={() => setShowEmailModal(null)}
          onSendInvoice={handleSendInvoiceEmail}
          onSendReminder={handleSendReminderEmail}
          sending={sendingEmail === showEmailModal?.id}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          invoice={showPaymentModal}
          language={language}
          onClose={() => setShowPaymentModal(null)}
          onSave={() => {
            setShowPaymentModal(null);
            loadData();
          }}
        />
      )}

      {/* Reminder Modal */}
      {showReminderModal && (
        <ReminderModal
          invoice={showReminderModal}
          language={language}
          onClose={() => setShowReminderModal(null)}
          onSave={() => {
            setShowReminderModal(null);
            loadData();
          }}
        />
      )}

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <TemplateEditor
          template={editingTemplate || {
            name: '',
            name_nl: '',
            description: '',
            description_nl: '',
            style_type: 'moderne',
            primary_color: '#8B4513',
            secondary_color: '#D2691E',
            accent_color: '#F5DEB3',
            background_color: '#FFFFFF',
            header_font: 'Helvetica',
            body_font: 'Helvetica',
            header_size: 24,
            body_size: 10,
            logo_position: 'top-left',
            logo_width: 50,
            logo_height: 25,
            show_qr_code: true,
            show_bank_info: true,
            is_default: false,
            elements: null // Let TemplateEditor use default elements
          }}
          onSave={handleSaveTemplate}
          onClose={() => {
            setShowTemplateEditor(false);
            setEditingTemplate(null);
          }}
          language={language}
        />
      )}

      {/* Advanced Template Editor Modal */}
      {showAdvancedEditor && (
        <AdvancedTemplateEditor
          template={editingTemplate || { name: '', blocks: [] }}
          onSave={(templateData) => {
            handleSaveTemplate(templateData);
            setShowAdvancedEditor(false);
            setEditingTemplate(null);
          }}
          onClose={() => {
            setShowAdvancedEditor(false);
            setEditingTemplate(null);
          }}
          language={language}
          documentType={advancedEditorType}
        />
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center bg-amber-50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {language === 'fr' ? 'Détails de la facture' : 'Factuurgegevens'}
                </h3>
                <p className="text-amber-700 font-mono font-bold">{selectedInvoice.invoice_number}</p>
              </div>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Status badges */}
              <div className="flex flex-wrap gap-3 mb-6">
                <StatusBadge status={selectedInvoice.status} language={language} />
                <SendStatusBadge 
                  status={selectedInvoice.send_status || 'not_sent'} 
                  language={language}
                  onChangeStatus={(newStatus) => {
                    handleUpdateSendStatus(selectedInvoice.id, newStatus);
                    setSelectedInvoice({ ...selectedInvoice, send_status: newStatus });
                  }}
                />
                {selectedInvoice.auto_generated && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    {t.autoGenerated}
                  </span>
                )}
              </div>

              {/* Dates & Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">{t.date}</p>
                  <p className="font-semibold">{selectedInvoice.invoice_date}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">{t.dueDate}</p>
                  <p className="font-semibold">{selectedInvoice.due_date}</p>
                </div>
                {selectedInvoice.order_reference && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-600">{language === 'fr' ? 'Réf. Commande' : 'Bestelref.'}</p>
                    <p className="font-semibold text-blue-700">{selectedInvoice.order_reference}</p>
                  </div>
                )}
              </div>

              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">{t.customer}</h4>
                <p className="font-medium">{selectedInvoice.customer?.name}</p>
                <p className="text-gray-600">{selectedInvoice.customer?.address}</p>
                <p className="text-gray-600">{selectedInvoice.customer?.postal_code} {selectedInvoice.customer?.city}</p>
                <p className="text-gray-600">{selectedInvoice.customer?.email}</p>
                {selectedInvoice.customer?.vat_number && (
                  <p className="text-gray-600 mt-2">TVA: {selectedInvoice.customer?.vat_number}</p>
                )}
              </div>

              {/* Items Table */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">
                  {language === 'fr' ? 'Articles' : 'Artikelen'}
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'Description' : 'Beschrijving'}
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'Taille' : 'Maat'}
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'Couleur' : 'Kleur'}
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'Qté' : 'Aantal'}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'P.U. HT' : 'Eenheid excl.'}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'TVA' : 'BTW'}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'Total HT' : 'Totaal excl.'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedInvoice.items?.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{item.description}</td>
                          <td className="px-4 py-3 text-center">
                            {item.size ? (
                              <span className="bg-gray-100 px-2 py-1 rounded text-sm font-medium">
                                {item.size}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {item.color ? (
                              <span className="bg-gray-100 px-2 py-1 rounded text-sm">
                                {item.color}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">{item.quantity}</td>
                          <td className="px-4 py-3 text-right">€{item.unit_price?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">{item.vat_rate}%</td>
                          <td className="px-4 py-3 text-right font-medium">€{item.total_excl?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="flex justify-end mb-6">
                <div className="w-72">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">{language === 'fr' ? 'Sous-total HT' : 'Subtotaal excl.'}</span>
                    <span className="font-medium">€{selectedInvoice.subtotal?.toFixed(2)}</span>
                  </div>
                  {selectedInvoice.vat_breakdown?.map((vat, idx) => (
                    <div key={idx} className="flex justify-between py-2 border-b text-sm">
                      <span className="text-gray-600">TVA {vat.rate}%</span>
                      <span>€{vat.amount?.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-3 bg-amber-50 px-3 rounded-lg mt-2">
                    <span className="font-bold text-lg">{language === 'fr' ? 'Total TTC' : 'Totaal incl.'}</span>
                    <span className="font-bold text-lg text-amber-700">€{selectedInvoice.total_incl?.toFixed(2)}</span>
                  </div>
                  {selectedInvoice.amount_paid > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex justify-between py-1 text-green-600">
                        <span>{t.paid}</span>
                        <span>€{selectedInvoice.amount_paid?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between py-1 font-semibold">
                        <span>{t.due}</span>
                        <span className="text-red-600">€{selectedInvoice.amount_due?.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payments History */}
              {selectedInvoice.payments?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {language === 'fr' ? 'Historique des paiements' : 'Betalingsgeschiedenis'}
                  </h4>
                  <div className="space-y-2">
                    {selectedInvoice.payments.map((payment, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-green-50 rounded-lg p-3">
                        <div>
                          <span className="font-medium text-green-700">€{payment.amount?.toFixed(2)}</span>
                          <span className="text-sm text-gray-600 ml-3">{payment.date}</span>
                          {payment.reference && (
                            <span className="text-sm text-gray-500 ml-2">({payment.reference})</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedInvoice.notes && (
                <div className="bg-yellow-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                  <p className="text-gray-700">{selectedInvoice.notes}</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50 flex justify-between">
              <div className="flex gap-2">
                <button
                  onClick={() => handleDownloadPdf(selectedInvoice.id, selectedInvoice.invoice_number)}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  PDF
                </button>
                {selectedInvoice.customer?.email && emailStatus.configured && (
                  <button
                    onClick={() => {
                      setShowEmailModal(selectedInvoice);
                      setSelectedInvoice(null);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </button>
                )}
                {selectedInvoice.status === 'draft' && (
                  <button
                    onClick={() => {
                      if (window.confirm(language === 'fr' ? 'Supprimer cette facture ?' : 'Deze factuur verwijderen?')) {
                        handleDeleteInvoice(selectedInvoice.id);
                        setSelectedInvoice(null);
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t.delete}
                  </button>
                )}
              </div>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                {language === 'fr' ? 'Fermer' : 'Sluiten'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



export default InvoicingPage;
